import { supabase } from "@/integrations/supabase/client";
import type { Collection } from "@/types/ecommerce";

export interface CollectionWithCount extends Collection {
  product_count: number;
}

export const collectionsService = {
  async list(): Promise<Collection[]> {
    const { data, error } = await supabase
      .from("collections")
      .select("*")
      .eq("is_active", true)
      .order("sort_order", { ascending: true });
    if (error) throw error;
    return (data ?? []) as unknown as Collection[];
  },

  async listAdmin(): Promise<CollectionWithCount[]> {
    const { data, error } = await supabase
      .from("collections")
      .select("*, products:products!products_collection_id_fkey(id)")
      .order("sort_order", { ascending: true });
    if (error) throw error;
    return ((data ?? []) as any[]).map((c) => ({
      ...c,
      product_count: Array.isArray(c.products) ? c.products.length : 0,
      products: undefined,
    })) as CollectionWithCount[];
  },

  async getBySlug(slug: string): Promise<Collection | null> {
    const { data, error } = await supabase.from("collections").select("*").eq("slug", slug).maybeSingle();
    if (error) throw error;
    return data as unknown as Collection | null;
  },

  async create(input: Partial<Collection> & { name: string; slug: string }) {
    const { data, error } = await supabase.from("collections").insert(input as any).select().single();
    if (error) throw error;
    return data as unknown as Collection;
  },

  async update(id: string, patch: Partial<Collection>) {
    const { data, error } = await supabase
      .from("collections")
      .update(patch as any)
      .eq("id", id)
      .select()
      .single();
    if (error) throw error;
    return data as unknown as Collection;
  },

  async remove(id: string) {
    // Block deletion if products are linked
    const { count, error: cErr } = await supabase
      .from("products")
      .select("id", { count: "exact", head: true })
      .eq("collection_id", id);
    if (cErr) throw cErr;
    if ((count ?? 0) > 0) {
      throw new Error(
        `Não é possível excluir: existem ${count} produto(s) vinculados a esta coleção. Desvincule-os primeiro.`,
      );
    }
    const { error } = await supabase.from("collections").delete().eq("id", id);
    if (error) throw error;
  },

  async uploadImage(file: File, kind: "cover" | "banner" = "cover"): Promise<string> {
    const ext = file.name.split(".").pop() ?? "jpg";
    const path = `${kind}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
    const { error } = await supabase.storage.from("collection-images").upload(path, file, {
      cacheControl: "3600",
      upsert: false,
    });
    if (error) throw error;
    const { data } = supabase.storage.from("collection-images").getPublicUrl(path);
    return data.publicUrl;
  },

  async reorder(orderedIds: string[]): Promise<void> {
    await Promise.all(
      orderedIds.map((id, index) =>
        supabase
          .from("collections")
          .update({ sort_order: (index + 1) * 10 })
          .eq("id", id),
      ),
    );
  },
};